/**
 * MeetingsPage — month calendar (shared MonthCalendar, same grid as the
 * compliance calendar) as the primary view + a list view toggle. Click a day
 * → that day's meetings below the grid; click a meeting → detail modal with
 * edit/cancel/done (organizer or meetings.edit_all). Create/edit via
 * MeetingFormModal. View choice persists per user in this browser.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { CalendarDays, Calendar, List, Plus, MapPin, Link2, Users, XCircle, CheckCircle2 } from 'lucide-react';
import {
  MEETING_STATUS_LABELS,
  type CreateMeetingInput, type EngagementOutput,
  type MeetingOutput, type RsvpStatus, type UpdateMeetingInput, type UserOutput,
} from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { MonthCalendar, dayKey } from '../components/MonthCalendar.js';
import { MeetingFormModal } from '../components/MeetingFormModal.js';
import { FilterBar } from '../components/FilterBar.js';
import { TableSkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { fmtDate } from '../lib/date.js';
import { useUserPreference } from '../lib/user-preferences.js';

type ModalMode = { kind: 'new'; presetDate?: string } | { kind: 'edit'; meeting: MeetingOutput } | null;

const STATUS_PILL: Record<string, string> = {
  SCHEDULED: 'pill-info',
  DONE: 'pill-success',
  CANCELLED: 'pill-neutral',
};

const RSVP_PILL: Record<RsvpStatus, string> = {
  PENDING: 'bg-bg-hover text-muted',
  ACCEPTED: 'bg-emerald-500/15 text-emerald-300',
  DECLINED: 'bg-rose-500/15 text-rose-300',
};

const RSVP_LABEL: Record<RsvpStatus, string> = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
};

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await authenticatedFetch(url, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${getAccessToken() ?? ''}`,
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) throw await httpError(res);
  return res.json() as Promise<T>;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function fmtDateTime(iso: string): string {
  return `${new Date(iso).toLocaleDateString('en-GB', { weekday: 'short' })}, ${fmtDate(iso)} ${fmtTime(iso)}`;
}

export function MeetingsPage() {
  const { user: currentUser } = useAuth();
  const { canDo } = useCapabilities();
  const queryClient = useQueryClient();
  const [view, setView] = useUserPreference<'calendar' | 'list'>('meetings.view', 'calendar', {
    validate: (value) => value === 'calendar' || value === 'list',
  });
  const [modal, setModal] = useState<ModalMode>(null);
  const [detail, setDetail] = useState<MeetingOutput | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [selected, setSelected] = useState<string>(dayKey(today));

  // List-view filters (search intentionally remains per visit).
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useUserPreference('meetings.status', '');
  const [mineOnly, setMineOnly] = useUserPreference('meetings.mineOnly', false);

  const list = useQuery({
    queryKey: ['meetings'],
    queryFn: async () => {
      const body = await fetchJson<{ ok: true; data: { items: MeetingOutput[] } }>('/v1/meetings?limit=500');
      return body.data.items;
    },
  });

  const users = useQuery({
    queryKey: ['users', { limit: 100 }],
    queryFn: async () => {
      const body = await fetchJson<{ ok: true; data: { items: UserOutput[] } }>('/v1/users?limit=100');
      return body.data.items;
    },
  });

  const engagements = useQuery({
    queryKey: ['engagements', { for: 'meeting-picker' }],
    queryFn: async () => {
      const body = await fetchJson<{ ok: true; data: { items: EngagementOutput[] } }>('/v1/engagements?limit=100&all=true&sortBy=updatedAt&sortOrder=desc');
      return body.data.items;
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['meetings'] });

  const create = useMutation({
    mutationFn: (input: CreateMeetingInput) => fetchJson('/v1/meetings', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => { invalidate(); setModal(null); setFormError(null); },
    onError: (e) => setFormError(e instanceof Error ? e.message : 'Create failed'),
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateMeetingInput }) =>
      fetchJson(`/v1/meetings/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: () => { invalidate(); setModal(null); setDetail(null); setFormError(null); },
    onError: (e) => setFormError(e instanceof Error ? e.message : 'Save failed'),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => fetchJson(`/v1/meetings/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => { invalidate(); setDetail(null); },
  });

  const done = useMutation({
    mutationFn: (id: string) => fetchJson(`/v1/meetings/${id}/done`, { method: 'POST' }),
    onSuccess: () => { invalidate(); setDetail(null); },
  });

  const remove = useMutation({
    mutationFn: (id: string) => fetchJson(`/v1/meetings/${id}`, { method: 'DELETE' }),
    onSuccess: () => { invalidate(); setDetail(null); },
  });

  const filtered = useMemo(() => {
    let items = list.data ?? [];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      items = items.filter((m) =>
        m.title.toLowerCase().includes(q) ||
        (m.clientName ?? '').toLowerCase().includes(q) ||
        (m.location ?? '').toLowerCase().includes(q));
    }
    if (statusFilter) items = items.filter((m) => m.status === statusFilter);
    if (mineOnly && currentUser) {
      items = items.filter((m) => m.organizerId === currentUser.id || m.attendees.some((a) => a.userId === currentUser.id));
    }
    return items;
  }, [list.data, search, statusFilter, mineOnly, currentUser]);

  function switchView(v: 'calendar' | 'list') {
    setView(v);
  }

  function canChange(m: MeetingOutput): boolean {
    return m.organizerId === currentUser?.id || canDo('meetings.edit_all');
  }

  const selectedMeetings = (list.data ?? [])
    .filter((m) => dayKey(new Date(m.startAt)) === selected)
    .sort((a, b) => a.startAt.localeCompare(b.startAt));

  const activeFilters = [
    ...(statusFilter ? [{ key: 'status', label: `Status: ${MEETING_STATUS_LABELS[statusFilter as keyof typeof MEETING_STATUS_LABELS]}` }] : []),
    ...(mineOnly ? [{ key: 'mine', label: 'Mine only' }] : []),
  ];

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-white">Meetings</h1>
          <p className="text-xs text-muted mt-0.5">Schedule meetings, invite staff and clients, get reminded 1 hour before.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button
              onClick={() => switchView('calendar')}
              className={clsx('px-3 py-1.5 text-xs flex items-center gap-1.5', view === 'calendar' ? 'bg-bg-hover text-white' : 'text-muted hover:text-white')}
            >
              <Calendar size={14} /> Calendar
            </button>
            <button
              onClick={() => switchView('list')}
              className={clsx('px-3 py-1.5 text-xs flex items-center gap-1.5', view === 'list' ? 'bg-bg-hover text-white' : 'text-muted hover:text-white')}
            >
              <List size={14} /> List
            </button>
          </div>
          {canDo('meetings.create') && (
            <button
              onClick={() => { setFormError(null); setModal({ kind: 'new' }); }}
              className="bg-accent hover:opacity-90 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg flex items-center gap-1.5"
            >
              <Plus size={15} /> New meeting
            </button>
          )}
        </div>
      </header>

      {view === 'calendar' ? (
        <div className="space-y-4">
          <MonthCalendar
            items={list.data}
            isLoading={list.isLoading}
            error={(list.error as Error) ?? null}
            dateOf={(m) => new Date(m.startAt)}
            chipKey={(m) => m.id}
            selected={selected}
            onSelect={setSelected}
            renderChip={(m) => (
              <button
                onClick={(e) => { e.stopPropagation(); setDetail(m); }}
                title={`${m.title}${m.clientName ? ` — ${m.clientName}` : ''}`}
                className={clsx(
                  'block w-full truncate text-left text-[11px] leading-4 px-1 rounded border',
                  m.status === 'CANCELLED'
                    ? 'border-border text-muted line-through'
                    : m.status === 'DONE'
                      ? 'border-emerald-700 text-emerald-300'
                      : 'border-border text-muted hover:border-accent hover:text-white',
                )}
              >
                {fmtTime(m.startAt)} {m.title}
              </button>
            )}
          />

          {!list.isLoading && !list.error && (
            <div className="bg-bg-panel border border-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-white">
                  {new Date(selected + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long' })}, {fmtDate(selected + 'T00:00:00')}
                  <span className="text-xs text-muted font-normal ml-2">{selectedMeetings.length} meeting{selectedMeetings.length === 1 ? '' : 's'}</span>
                </h3>
                {canDo('meetings.create') && (
                  <button
                    onClick={() => { setFormError(null); setModal({ kind: 'new', presetDate: selected }); }}
                    className="text-xs text-accent hover:underline flex items-center gap-1"
                  >
                    <Plus size={12} /> Add on this day
                  </button>
                )}
              </div>
              {selectedMeetings.length === 0 ? (
                <p className="text-sm text-muted italic">No meetings this day.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {selectedMeetings.map((m) => (
                    <li key={m.id} className="py-2.5 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <button onClick={() => setDetail(m)} className={clsx('text-sm text-white hover:text-accent block truncate', m.status === 'CANCELLED' && 'line-through text-muted')}>
                          {m.title}
                        </button>
                        <p className="text-xs text-muted truncate">
                          {fmtTime(m.startAt)} · {m.durationMin} min{m.clientName ? ` · ${m.clientName}` : ''} · {m.organizerName}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs border flex-shrink-0 ${STATUS_PILL[m.status] ?? 'pill-neutral'}`}>
                        {MEETING_STATUS_LABELS[m.status]}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {(list.data ?? []).length === 0 && !list.isLoading && !list.error && (
            <div className="bg-bg-panel border border-border rounded-xl">
              <EmptyState
                icon={CalendarDays}
                title="No meetings yet."
                subtitle="Schedule your first meeting — attendees get a calendar invite by email."
                action={canDo('meetings.create') ? { label: 'New meeting', onClick: () => setModal({ kind: 'new' }) } : undefined}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <FilterBar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search title, client, location…"
            activeFilters={activeFilters}
            onRemoveFilter={(key) => {
              if (key === 'status') setStatusFilter('');
              if (key === 'mine') setMineOnly(false);
            }}
            onClearAll={() => { setStatusFilter(''); setMineOnly(false); setSearch(''); }}
          >
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-bg-panel border border-border rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:border-accent"
            >
              <option value="">All statuses</option>
              {Object.entries(MEETING_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-sm text-muted px-2">
              <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} className="accent-emerald-500" />
              Mine only
            </label>
          </FilterBar>

          <div className="bg-bg-panel border border-border rounded-xl overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="text-left text-xs text-muted border-b border-border">
                  <th className="px-4 py-2.5 font-medium">When</th>
                  <th className="px-4 py-2.5 font-medium">Title</th>
                  <th className="px-4 py-2.5 font-medium">Client</th>
                  <th className="px-4 py-2.5 font-medium">Organizer</th>
                  <th className="px-4 py-2.5 font-medium">Attendees</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.isLoading ? (
                  <TableSkeletonRows colSpan={6} rows={5} />
                ) : list.error ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-rose-400 text-sm">{(list.error as Error).message}</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted text-sm italic">No meetings match these filters.</td></tr>
                ) : (
                  filtered.map((m) => (
                    <tr key={m.id} className="hover:bg-bg-hover/40 cursor-pointer" onClick={() => setDetail(m)}>
                      <td className="px-4 py-2.5 text-muted whitespace-nowrap">{fmtDateTime(m.startAt)}</td>
                      <td className={clsx('px-4 py-2.5 text-white', m.status === 'CANCELLED' && 'line-through text-muted')}>{m.title}</td>
                      <td className="px-4 py-2.5 text-muted">{m.clientName ?? '—'}</td>
                      <td className="px-4 py-2.5 text-muted">{m.organizerName}</td>
                      <td className="px-4 py-2.5 text-muted">{m.attendees.length}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded text-xs border ${STATUS_PILL[m.status] ?? 'pill-neutral'}`}>
                          {MEETING_STATUS_LABELS[m.status]}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <MeetingFormModal
          initial={modal.kind === 'edit' ? modal.meeting : null}
          presetDate={modal.kind === 'new' ? modal.presetDate : undefined}
          users={users.data ?? []}
          engagements={engagements.data ?? []}
          onClose={() => { setModal(null); setFormError(null); }}
          onSubmit={(input) => {
            setFormError(null);
            if (modal.kind === 'edit') update.mutate({ id: modal.meeting.id, input: input as UpdateMeetingInput });
            else create.mutate(input as CreateMeetingInput);
          }}
          isPending={create.isPending || update.isPending}
          error={formError}
        />
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setDetail(null)}>
          <div className="bg-bg-panel border border-border rounded-xl w-full max-w-lg mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div className="min-w-0">
                <h2 className={clsx('text-base font-semibold text-white', detail.status === 'CANCELLED' && 'line-through')}>{detail.title}</h2>
                <p className="text-xs text-muted mt-0.5">{fmtDateTime(detail.startAt)} · {detail.durationMin} min</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`px-2 py-0.5 rounded text-xs border ${STATUS_PILL[detail.status] ?? 'pill-neutral'}`}>
                  {MEETING_STATUS_LABELS[detail.status]}
                </span>
                <button onClick={() => setDetail(null)} className="text-muted hover:text-white">✕</button>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              {detail.location && (
                <p className="flex items-center gap-2 text-white"><MapPin size={14} className="text-muted flex-shrink-0" />{detail.location}</p>
              )}
              {detail.meetingUrl && (
                <p className="flex items-center gap-2 min-w-0">
                  <Link2 size={14} className="text-muted flex-shrink-0" />
                  <a href={detail.meetingUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline truncate">{detail.meetingUrl}</a>
                </p>
              )}
              <p className="text-muted">Organizer: <span className="text-white">{detail.organizerName}</span></p>
              {detail.clientName && <p className="text-muted">Client: <span className="text-white">{detail.clientName}</span></p>}
              {detail.engagementTitle && <p className="text-muted">Engagement: <span className="text-white">{detail.engagementTitle}</span></p>}
              <div>
                <p className="text-muted flex items-center gap-1.5 mb-1"><Users size={14} /> Attendees</p>
                <ul className="space-y-0.5 pl-5">
                  {detail.attendees.map((a) => (
                    <li key={a.id} className="text-xs text-white list-disc">
                      {a.userName ?? a.email}
                      {a.userId === detail.organizerId && <span className="text-muted"> (organizer)</span>}
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${RSVP_PILL[a.rsvpStatus]}`}>
                        {RSVP_LABEL[a.rsvpStatus]}
                      </span>
                      {a.reminderSentAt && <span className="text-muted"> · reminded</span>}
                    </li>
                  ))}
                </ul>
              </div>
              {detail.notes && (
                <div className="bg-bg-card border border-border rounded-lg p-3 text-xs text-white whitespace-pre-wrap">{detail.notes}</div>
              )}
            </div>

            {canChange(detail) && detail.status === 'SCHEDULED' && (
              <div className="flex flex-wrap justify-end gap-2 mt-4 pt-3 border-t border-border">
                <button
                  onClick={() => done.mutate(detail.id)}
                  disabled={done.isPending}
                  className="btn-ghost-accent text-xs px-3 py-1.5 rounded-lg flex items-center gap-1"
                >
                  <CheckCircle2 size={13} /> Mark done
                </button>
                <button
                  onClick={() => { setFormError(null); setModal({ kind: 'edit', meeting: detail }); }}
                  className="text-xs text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg"
                >
                  Edit
                </button>
                <button
                  onClick={() => cancel.mutate(detail.id)}
                  disabled={cancel.isPending}
                  className="btn-ghost-danger text-xs px-3 py-1.5 rounded-lg flex items-center gap-1"
                >
                  <XCircle size={13} /> Cancel meeting
                </button>
              </div>
            )}
            {canChange(detail) && (
              <div className="flex flex-wrap justify-end gap-2 mt-2">
                <button
                  onClick={() => {
                    if (window.confirm(`Delete meeting “${detail.title}”? Soft-deleted — restorable from Admin → Trash. Cancel keeps a visible record; delete is for mistakes.`)) {
                      remove.mutate(detail.id);
                    }
                  }}
                  disabled={remove.isPending}
                  className="text-xs text-muted hover:text-rose-300 px-3 py-1.5"
                >
                  Delete meeting…
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
